// src/ui/hud.js
import { cssToPhaser, Theme } from "../config/theme.js";
import { Collection } from "../game/collection.js";

// ─────────────────────────────────────────────────────────────────────────────
// Full-width HUD strip (y = 0 → 205) — sits entirely above the board (y = 210).
// Includes 30px safe-area inset (T) for iPhone notch / Dynamic Island.
//
// Layout — 3-column grid:
//   y=0   ▐ neon top line (3px)
//   y=3   │ Lv X  (left)    ✦ X/Y (centre)     XP X/Y (right)
//   y=20  ▐ full-width XP bar (4px)
//   y=26  ▐ separator line (2px)
//         │ vertical dividers at W/3 and 2W/3  (y=28→98)
//   y=28  │ [MOVES pill]   [+N Osaka incr]   [OBJETIVO label]
//   y=33  │  JOGADAS label  (incr text)        OBJETIVO label
//   y=46  │  number 40px   [SCORE 52px Kaze]  [type badge 14px]
//   y=66  │                                   [progress 13px]
//   y=100 ▐ goal bar (10px, element-coloured)
//   y=112 ▐ score bar (5px, optional)
//   y=121 │ [status / instr]   ⏱ timer (centre)   Combo (right)
//   y=138 │ status text
//   y=175 └──
//   y=180   [BOARD]
// ─────────────────────────────────────────────────────────────────────────────

// Helper: format score as "1.234.567"
function fmtScore(n) {
  return String(Math.floor(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

// Element → bar fill colour
const ELEM_BAR_CLR = {
  red:    0xef4444,
  yellow: 0xfacc15,
  green:  0x4ade80,
  blue:   0x38bdf8,
  purple: 0xc084fc,
};

export class HUD {
  constructor(scene, { type = 'normal' } = {}) {
    this.scene = scene;
    this.hudType = type;
    const W = scene.scale.width;
    this.barMaxW = W - 28;
    const D = 1000;

    // Safe-area inset for devices with notch/Dynamic Island (iPhone X+)
    const T = 30;

    const f = () => Theme.hud.fontFamily;

    // Column geometry
    const divX1 = Math.floor(W / 3);        // ~180
    const divX2 = Math.floor(2 * W / 3);    // ~360
    const cx1   = Math.floor(divX1 / 2);          // ~90  (left col centre)
    const cx2   = Math.floor(W / 2);               // 270  (mid col centre)
    const cx3   = Math.floor(divX2 + (W - divX2) / 2); // ~450 (right col centre)

    // ── Panel background ──────────────────────────────────────────────────
    this.panel = scene.add
      .rectangle(0, 0, W, 175 + T, cssToPhaser(Theme.hud.panelColor), 0.22)
      .setOrigin(0, 0).setDepth(D);

    // ── Neon top line ─────────────────────────────────────────────────────
    this.topLine = scene.add
      .rectangle(0, T, W, 3, cssToPhaser(Theme.hud.accentLine), 0.9)
      .setOrigin(0, 0).setDepth(D + 1);

    // ── Row 1: Level · Fragment · XP ─────────────────────────────────────
    this.levelText = scene.add.text(14, T + 6, "EQUIPA Lv 1", {
      fontFamily: f(), fontSize: "11px", color: Theme.hud.textMuted,
    }).setDepth(D + 2);

    // Fragment counter — brighter, bigger
    const prog0 = Collection.getFragmentProgress();
    this.fragmentText = scene.add.text(cx2, T + 4, `✦ ${prog0.current}/${prog0.needed}`, {
      fontFamily: f(), fontSize: "18px", color: "#93c5fd",
    }).setOrigin(0.5, 0).setDepth(D + 2);

    this.xpText = scene.add.text(W - 14, T + 6, "XP 0/100", {
      fontFamily: f(), fontSize: "11px", color: Theme.hud.textMuted,
    }).setOrigin(1, 0).setDepth(D + 2);

    // ── Full-width XP progress bar ────────────────────────────────────────
    this.xpBarBg = scene.add
      .rectangle(14, T + 20, W - 28, 4, 0x1e293b, 1)
      .setOrigin(0, 0).setDepth(D + 1);
    this.xpBarFill = scene.add
      .rectangle(14, T + 20, 2, 4, 0x6366f1, 1)
      .setOrigin(0, 0).setDepth(D + 2);

    // ── Separator ─────────────────────────────────────────────────────────
    this.separatorLine = scene.add
      .rectangle(0, T + 26, W, 2, cssToPhaser(Theme.hud.accentLine), 0.35)
      .setOrigin(0, 0).setDepth(D + 1);


    this.movesLabel = scene.add.text(cx1, T + 34, "JOGADAS", {
      fontFamily: f(), fontSize: "10px", color: Theme.hud.textMuted,
    }).setOrigin(0.5, 0).setDepth(D + 2);

    this.movesText = scene.add.text(cx1, T + 46, "0", {
      fontFamily: f(), fontSize: "40px", color: Theme.hud.textMain,
      fontStyle: "bold",
    }).setOrigin(0.5, 0).setDepth(D + 2);

    // ── Centre column: score increment + SCORE Kamikaze ───────────────────
    this._scoreCounter = { value: 0 };
    this._scoreTween   = null;

    // Score increment (Osaka) — floats above score
    this.incrText = scene.add.text(cx2, T + 32, "", {
      fontFamily: "Osaka, system-ui, Arial",
      fontSize: "20px",
      color: "#facc15",
      stroke: "#0b0f14",
      strokeThickness: 3,
    }).setOrigin(0.5, 0).setDepth(D + 3).setAlpha(0);
    this._incrFadeTimer = null;

    // Score — hero element: 52px Kamikaze, no stroke
    this.scoreText = scene.add.text(cx2, T + 46, "0", {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize: "52px",
      color: Theme.hud.textMain,
    }).setOrigin(0.5, 0).setDepth(D + 2);

    // ── Right column: OBJECTIVE ───────────────────────────────────────────
    this.goalLabel = scene.add.text(cx3, T + 34, "OBJETIVO", {
      fontFamily: f(), fontSize: "10px", color: Theme.hud.textMuted,
    }).setOrigin(0.5, 0).setDepth(D + 2);

    // Type badge (larger, element-coloured)
    this.goalColorText = scene.add.text(cx3, T + 47, "—", {
      fontFamily: f(), fontSize: "14px", color: Theme.hud.textMain,
      fontStyle: "bold",
    }).setOrigin(0.5, 0).setDepth(D + 2);

    // Progress counter
    this.goalProgressText = scene.add.text(cx3, T + 66, "", {
      fontFamily: f(), fontSize: "13px", color: Theme.hud.textMuted,
    }).setOrigin(0.5, 0).setDepth(D + 2);

    // ── Goal progress bar (10px, element-coloured) ─────────────────────────
    this.goalBarBg = scene.add
      .rectangle(14, T + 100, this.barMaxW, 10, cssToPhaser(Theme.hud.goalBarBgColor), 1)
      .setOrigin(0, 0).setDepth(D + 1);

    this.goalBarFill = scene.add
      .rectangle(14, T + 100, 2, 10, cssToPhaser(Theme.hud.goalBarColor), 1)
      .setOrigin(0, 0).setDepth(D + 2);

    // ── Score progress bar (optional, 5px) ────────────────────────────────
    this._scoreGoal = null;
    this.scoreBarBg = scene.add
      .rectangle(14, T + 112, this.barMaxW, 5, cssToPhaser(Theme.hud.goalBarBgColor), 1)
      .setOrigin(0, 0).setDepth(D + 1).setVisible(false);

    this.scoreBarFill = scene.add
      .rectangle(14, T + 112, 2, 5, 0xfacc15, 1)
      .setOrigin(0, 0).setDepth(D + 2).setVisible(false);

    // ── Bottom row: combo (right) · timer (centre) ─────────────────────────
    this.comboText = scene.add.text(W - 14, T + 121, "Combo máx: x1", {
      fontFamily: f(), fontSize: "13px", color: Theme.hud.textAccent,
    }).setOrigin(1, 0).setDepth(D + 2);

    this.timerText = scene.add.text(W / 2, T + 119, "⏱ 0:00", {
      fontFamily: f(), fontSize: "18px", color: "#94a3b8",
    }).setOrigin(0.5, 0).setDepth(D + 2).setVisible(false);

    // ── Status text ───────────────────────────────────────────────────────
    this.statusText = scene.add.text(14, T + 138, "", {
      fontFamily: f(), fontSize: "12px", color: Theme.hud.textStatus,
      wordWrap: { width: this.barMaxW },
      lineSpacing: 2,
    }).setDepth(D + 2);

    // ── All elements for setVisible ───────────────────────────────────────
    this._ui = [
      this.panel, this.topLine, this.separatorLine,
      this.levelText, this.xpText, this.fragmentText,
      this.xpBarBg, this.xpBarFill,
      this.movesLabel, this.movesText,
      this.incrText, this.scoreText,
      this.goalLabel, this.goalColorText, this.goalProgressText,
      this.goalBarBg, this.goalBarFill,
      this.scoreBarBg, this.scoreBarFill,
      this.comboText, this.timerText,
      this.statusText,
    ];
  }

  // ── Live theme update ──────────────────────────────────────────────────────
  applyTheme() {
    const font = Theme.hud.fontFamily;

    this.panel.setFillStyle(cssToPhaser(Theme.hud.panelColor), 1);
    this.topLine.setFillStyle(cssToPhaser(Theme.hud.accentLine), 0.9);
    this.separatorLine.setFillStyle(cssToPhaser(Theme.hud.accentLine), 0.35);
    this.goalBarBg.setFillStyle(cssToPhaser(Theme.hud.goalBarBgColor), 1);
    this.goalBarFill.setFillStyle(cssToPhaser(Theme.hud.goalBarColor), 1);
    this.scoreBarBg.setFillStyle(cssToPhaser(Theme.hud.goalBarBgColor), 1);

    this.levelText.setColor(Theme.hud.textMuted).setFontFamily(font);
    this.xpText.setColor(Theme.hud.textMuted).setFontFamily(font);
    this.movesLabel.setColor(Theme.hud.textMuted).setFontFamily(font);
    this.movesText.setFontFamily(font);
    this.goalLabel.setColor(Theme.hud.textMuted).setFontFamily(font);
    this.goalColorText.setFontFamily(font);
    this.goalProgressText.setColor(Theme.hud.textMuted).setFontFamily(font);
    this.scoreText.setColor(Theme.hud.textMain);
    this.comboText.setColor(Theme.hud.textAccent).setFontFamily(font);
    this.statusText.setColor(Theme.hud.textStatus).setFontFamily(font);
  }

  setVisible(v) {
    for (const o of this._ui) o.setVisible(!!v);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  setMoves(n) {
    this.movesText.setText(`${n}`);
    if (n <= 5 && n > 0) {
      this.movesText.setColor("#ef4444");
      this.scene.tweens.add({
        targets: this.movesText,
        scaleX: 1.25, scaleY: 1.25,
        duration: 80, yoyo: true, ease: "Quad.Out",
      });
    } else if (n <= 10 && n > 0) {
      this.movesText.setColor("#f97316");
    } else if (n <= 15 && n > 0) {
      this.movesText.setColor("#facc15");
    } else {
      this.movesText.setColor(Theme.hud.textMain);
    }
  }

  setGoal(goal, current, goal2, current2) {
    const colorMap = {
      red:    Theme.tile.colorRed,
      yellow: Theme.tile.colorYellow,
      green:  Theme.tile.colorGreen,
      blue:   Theme.tile.colorBlue,
      purple: Theme.tile.colorPurple,
    };

    if (!goal) {
      this.goalColorText.setText("—").setColor(Theme.hud.textMuted).setFontSize("14px");
      this.goalProgressText.setText("").setColor(Theme.hud.textMuted).setFontSize("13px");
      this.goalBarFill.width = 2;
      return;
    }

    // ── Dual collect goals ────────────────────────────────────────────────────
    if (goal.type === "collect" && goal2?.type === "collect") {
      const c1 = goal.color  || "";
      const c2 = goal2.color || "";
      const ratio1 = goal.count  > 0 ? Math.min(1, current  / goal.count)  : 0;
      const ratio2 = goal2.count > 0 ? Math.min(1, current2 / goal2.count) : 0;

      this.goalColorText
        .setText(`${c1.toUpperCase()}  ${current}/${goal.count}`)
        .setColor(colorMap[c1] || Theme.hud.textMain)
        .setFontSize("12px");

      this.goalProgressText
        .setText(`${c2.toUpperCase()}  ${current2}/${goal2.count}`)
        .setColor(colorMap[c2] || Theme.hud.textMuted)
        .setFontSize("12px");

      const ratio = Math.min(ratio1, ratio2);
      this.goalBarFill.width = Math.max(2, Math.floor(this.barMaxW * ratio));
      this.goalBarFill.setFillStyle(ELEM_BAR_CLR[c1] || cssToPhaser(Theme.hud.goalBarColor), 1);
      return;
    }

    // ── Single collect goal ───────────────────────────────────────────────────
    if (goal.type === "collect") {
      const c = goal.color || "";
      this.goalColorText.setText(c.toUpperCase())
        .setColor(colorMap[c] || Theme.hud.textMain)
        .setFontSize("14px");
      this.goalProgressText.setText(`${current} / ${goal.count}`)
        .setColor(Theme.hud.textMuted).setFontSize("13px");
      const ratio = goal.count > 0 ? Math.min(1, current / goal.count) : 0;
      this.goalBarFill.width = Math.max(2, Math.floor(this.barMaxW * ratio));
      this.goalBarFill.setFillStyle(ELEM_BAR_CLR[c] || cssToPhaser(Theme.hud.goalBarColor), 1);
      return;
    }

    if (goal.type === "score") {
      this.goalColorText.setText("SCORE").setColor(Theme.hud.textMain).setFontSize("14px");
      this.goalProgressText.setText(`${current} / ${goal.target}`)
        .setColor(Theme.hud.textMuted).setFontSize("13px");
      const ratio = goal.target > 0 ? Math.min(1, current / goal.target) : 0;
      this.goalBarFill.width = Math.max(2, Math.floor(this.barMaxW * ratio));
      this.goalBarFill.setFillStyle(cssToPhaser(Theme.hud.goalBarColor), 1);
      return;
    }

    if (goal.type === "collect_fragments") {
      this.goalColorText.setText("✦ FRAGMENTOS")
        .setColor("#60a5fa").setFontSize("12px");
      this.goalProgressText.setText(`${current} / ${goal.count}`)
        .setColor(Theme.hud.textMuted).setFontSize("13px");
      const ratio = goal.count > 0 ? Math.min(1, current / goal.count) : 0;
      this.goalBarFill.width = Math.max(2, Math.floor(this.barMaxW * ratio));
      this.goalBarFill.setFillStyle(0x60a5fa, 1);
      return;
    }


    // Fallback
    this.goalColorText.setText(String(goal.type || "—").toUpperCase())
      .setColor(Theme.hud.textMain).setFontSize("14px");
    this.goalProgressText.setText("").setFontSize("13px");
    this.goalBarFill.width = 2;
  }

  // ── Score progress bar ─────────────────────────────────────────────────────
  setScoreBar(scoreGoal) {
    this._scoreGoal = scoreGoal || null;
    const visible = !!scoreGoal;
    this.scoreBarBg.setVisible(visible);
    this.scoreBarFill.setVisible(visible);
    if (!visible) this.scoreBarFill.width = 2;
  }

  _updateScoreBar(currentScore) {
    if (!this._scoreGoal) return;
    const ratio = this._scoreGoal.target > 0
      ? Math.min(1, currentScore / this._scoreGoal.target)
      : 0;
    this.scoreBarFill.width = Math.max(2, Math.floor(this.barMaxW * ratio));
  }

  // Auto-size score font so long numbers never overflow the centre column
  _scoreFont(formatted) {
    const len = formatted.length;
    if (len <= 5) return "52px";   // up to 99.999
    if (len <= 7) return "44px";   // up to 9.999.999
    return "36px";                 // 8+ chars — millions+
  }

  // ── Score: Kamikaze, auto-sized, scale pop on update ──────────────────────
  setScore(score) {
    const target = Number(score) || 0;
    if (this._scoreTween) { this._scoreTween.stop(); this._scoreTween = null; }

    this._updateScoreBar(target);

    const counter = this._scoreCounter;
    this._scoreTween = this.scene.tweens.add({
      targets:  counter,
      value:    target,
      duration: 350,
      ease:     'Cubic.Out',
      onUpdate: () => {
        const fmt = fmtScore(counter.value);
        this.scoreText.setFontSize(this._scoreFont(fmt)).setText(fmt);
      },
      onComplete: () => {
        counter.value = target;
        const fmt = fmtScore(target);
        this.scoreText.setFontSize(this._scoreFont(fmt)).setText(fmt);
        this._scoreTween = null;
        // Brief scale pop on update
        this.scene.tweens.add({
          targets: this.scoreText, scaleX: 1.08, scaleY: 1.08,
          duration: 90, yoyo: true, ease: "Quad.Out",
        });
      },
    });
  }

  // ── Score increment: Osaka, centered above score, holds then fades ─────────
  showScoreIncrement(points, comboMult, bonus, bestSize) {
    if (!this.incrText) return;

    let label = `+${points}`;
    if (bonus > 0)       label += `  +${bonus}`;
    if (comboMult > 1)   label += `  ×${comboMult}`;
    if (bestSize >= 5)   label += `  🔥`;

    this.scene.tweens.killTweensOf(this.incrText);
    if (this._incrFadeTimer) { this._incrFadeTimer.remove(); this._incrFadeTimer = null; }

    this.incrText.setText(label).setAlpha(1);

    this._incrFadeTimer = this.scene.time.delayedCall(2200, () => {
      this.scene.tweens.add({
        targets:  this.incrText,
        alpha:    0,
        duration: 1200,
        ease:     'Sine.In',
        onComplete: () => { this._incrFadeTimer = null; },
      });
    });
  }

  setScoreColor(cssHex) {
    this.scoreText.setColor(cssHex);
  }

  setCombo(maxCombo) {
    const v = Math.max(1, Number(maxCombo || 1));
    this.comboText.setText(`Combo máx: x${v}`);
  }

  setStatus(msg) {
    this.statusText.setText(msg || "");
  }

  // ── Fragmentos de Prismara ────────────────────────────────────────────────
  setFragments(current, needed) {
    if (current === undefined || needed === undefined) {
      const prog = Collection.getFragmentProgress();
      current = prog.current;
      needed  = prog.needed;
    }
    this.fragmentText.setText(`✦ ${current}/${needed}`);

    if (arguments.length > 0) {
      this.scene.tweens.add({
        targets: this.fragmentText, scaleX: 1.3, scaleY: 1.3,
        duration: 120, yoyo: true, ease: "Quad.Out",
      });
    }
  }

  // ── Level + XP ────────────────────────────────────────────────────────────
  setLevel(lv, xp, nextXp) {
    this.levelText.setText(`EQUIPA Lv ${lv}`);
    this.xpText.setText(`XP ${xp} / ${nextXp}`);
    const ratio = nextXp > 0 ? Math.min(1, xp / nextXp) : 0;
    this.xpBarFill.width = Math.max(2, Math.floor((this.scene.scale.width - 28) * ratio));
  }

  // ── Timer ─────────────────────────────────────────────────────────────────
  setTimer(seconds) {
    if (!this.timerText.visible) this.timerText.setVisible(true);
    const s = Math.max(0, Math.floor(seconds));
    const mm = String(Math.floor(s / 60)).padStart(1, "0");
    const ss = String(s % 60).padStart(2, "0");
    this.timerText.setText(`⏱ ${mm}:${ss}`);
    if (s <= 10) {
      this.timerText.setColor("#ef4444");
      this.scene.tweens.add({ targets: this.timerText, scaleX: 1.15, scaleY: 1.15, duration: 80, yoyo: true });
    } else if (s <= 30) {
      this.timerText.setColor("#f97316");
    } else {
      this.timerText.setColor("#94a3b8");
    }
  }

  hideTimer() {
    this.timerText.setVisible(false);
  }

  // ── Stub — awaiting logo asset ─────────────────────────────────────────────
  setTitle(_txt) {}
}
